import React from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Button } from '@kontentino/ui';
import Modal from 'components/shared/Modal';
import PageTypeUtils from 'app/utils/pageType';
import type { ConnectSuccessMethod, RefusedProfile } from 'types/Page';
import SUCCESS_ILLUSTRATION from 'app/components/modals/firstTimeFlags/firstPostSaved/icon/first-post-illustration.svg';
import RefusedPagesList from './RefusedPagesList';

type Props = {
  // Profiles the confirm added or reconnected.
  count: number;
  method: ConnectSuccessMethod;
  // The network that was connected: the host's `type`, never the picker's
  // report (always Facebook today).
  pageType: number;
  refused: RefusedProfile[];
  // Back to the network grid. Left out where the host has none (Settings).
  onAddMore?: () => void;
  onClose: () => void;
  closeLabel: 'backToCalendar' | 'done';
};

// Full literal keys, so a search for a key finds its use.
const lineOf = (
  t: TFunction,
  method: ConnectSuccessMethod,
  pageType: number,
): string => {
  switch (method) {
    case 'safe_connect':
      return t('newSocialMediaProfile.success.viaSafeConnect');
    case 'page_admin':
      return t('newSocialMediaProfile.success.viaPageAdmin');
    default:
      return t('newSocialMediaProfile.success.viaNetwork', {
        network: PageTypeUtils.getLabel(pageType),
      });
  }
};

/**
 * What the connect modal shows once profiles were added, in place of the
 * picker, for every network and method: the high-five, how they were added,
 * the pages that could not be, and the way on.
 */
const ConnectSuccess = ({
  count,
  method,
  pageType,
  refused,
  onAddMore,
  onClose,
  closeLabel,
}: Props) => {
  const { t } = useTranslation();

  return (
    <>
      <Modal.Content>
        <div
          data-cy="connect-success"
          className="tw-flex tw-flex-col tw-items-center tw-text-center"
        >
          <img
            src={SUCCESS_ILLUSTRATION}
            alt=""
            data-cy="connect-success_illustration"
            className="tw-h-[140px] tw-w-[222px]"
          />
          <h2
            data-cy="connect-success_title"
            className="tw-m-0 tw-mt-4 tw-text-lg tw-font-semibold tw-text-grayscale-180"
          >
            {t('newSocialMediaProfile.success.title', { count })}
          </h2>
          <p
            data-cy="connect-success_line"
            className="tw-m-0 tw-mt-1 tw-text-md tw-text-grayscale-140"
          >
            {lineOf(t, method, pageType)}
          </p>
          {refused.length > 0 && (
            <RefusedPagesList
              refusedProfiles={refused}
              className="tw-mt-4 tw-w-full tw-rounded-md tw-bg-grayscale-5 tw-p-4 tw-text-left"
            />
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          variant="secondary"
          size="large"
          onClick={onClose}
          data-cy="connect-success_close"
        >
          {closeLabel === 'backToCalendar'
            ? t('newSocialMediaProfile.success.backToCalendar')
            : t('newSocialMediaProfile.success.done')}
        </Button>
        {onAddMore && (
          <Button
            variant="primary"
            size="large"
            onClick={onAddMore}
            data-cy="connect-success_add-more"
          >
            {t('newSocialMediaProfile.success.addMore')}
          </Button>
        )}
      </Modal.Footer>
    </>
  );
};

export default ConnectSuccess;
